import moment from "moment";

export const ACTIONS = {
  baseline: { label: "Baseline", color: "#475467", bg: "#F2F4F7", dot: "#98A2B3" },
  created: { label: "Created", color: "#027A48", bg: "#ECFDF3", dot: "#12B76A" },
  updated: { label: "Updated", color: "#175CD3", bg: "#EFF8FF", dot: "#2E90FA" },
  enabled: { label: "Enabled", color: "#0E7090", bg: "#ECFDFF", dot: "#06AED4" },
  disabled: { label: "Disabled", color: "#B54708", bg: "#FEF0C7", dot: "#F79009" },
  deleted: { label: "Deleted", color: "#B42318", bg: "#FEE4E2", dot: "#F04438" },
};

export const actionMeta = (action) => ACTIONS[action] || ACTIONS.updated;

// Fields the backend also ignores when diffing; hidden so full-record grids
// only show what an admin can actually edit.
export const SKIP_FIELDS = new Set([
  "_id",
  "__v",
  "clientId",
  "createdAt",
  "updatedAt",
  "createdBy",
  "updatedBy",
  "UserId",
]);

// What each kind of entry means when it has no field changes to list.
export const ACTION_SENTENCE = {
  baseline: "Starting point: the record as it was when tracking began. Nothing was changed in this version.",
  created: "This record was created.",
  deleted: "This record was deleted. Its last values are kept in the full record.",
  enabled: "This record was switched on.",
  disabled: "This record was switched off.",
};

export const STATUS_FIELDS = new Set(["isEnabled", "isActive"]);

const ACRONYMS = new Set(["idv", "cc", "rto", "tp", "od", "pa", "gst", "tppd", "ncb", "id", "tds", "pan", "url"]);

const capitalise = (word) => word.charAt(0).toUpperCase() + word.slice(1);

const humanizeSegment = (segment) => {
  if (/^\d+$/.test(segment)) return `#${Number(segment) + 1}`;
  if (STATUS_FIELDS.has(segment)) return "Status";
  return segment
    .replace(/_/g, " ")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/([A-Z]+)([A-Z][a-z])/g, "$1 $2")
    .split(/\s+/)
    .filter(Boolean)
    .map((word) => (ACRONYMS.has(word.toLowerCase()) ? word.toUpperCase() : capitalise(word)))
    .join(" ");
};

export const humanizeField = (path) => String(path).split(".").map(humanizeSegment).join(" › ");

export const formatDateTime = (value) => {
  const m = moment(value);
  return value && m.isValid() ? m.format("DD MMM YYYY, hh:mm A") : "—";
};

export const formatDay = (value) => {
  const m = moment(value);
  return value && m.isValid() ? m.format("DD MMM YYYY") : "—";
};

export const timeAgo = (value) => {
  const m = moment(value);
  return value && m.isValid() ? m.fromNow() : "";
};

export const initialsOf = (name) => {
  const parts = String(name || "").trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "?";
  return (parts[0][0] + (parts.length > 1 ? parts[parts.length - 1][0] : "")).toUpperCase();
};

const AVATAR_COLORS = ["#1E3A5F", "#3E6A96", "#0E7090", "#6941C6", "#B54708", "#027A48"];

export const avatarColor = (name) => {
  const text = String(name || "?");
  let hash = 0;
  for (let i = 0; i < text.length; i += 1) hash = (hash * 31 + text.charCodeAt(i)) >>> 0;
  return AVATAR_COLORS[hash % AVATAR_COLORS.length];
};

const ISO_DATE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/;
const HEX24 = /^[0-9a-fA-F]{24}$/;
const NUMERIC = /^-?\d+(\.\d+)?$/;
const PERCENT_FIELD = /(discount|percent)/i;
const MONEY_FIELD = /(idv|price|premium|rate|amount)/i;
// Named like money but unitless in this app: the Nil Dip figure is a rate
// (0.4 - 0.7) and a zone's rate is a base-rate multiplier.
const NOT_MONEY_FIELD = /(nildip|^rate$)/i;
const PLAIN_NUMBER_FIELD = /(year|code|number|phone|mobile|^cc$)/i;

const leafOf = (path) => String(path).split(".").pop();

export const isEmptyValue = (value) =>
  value === null ||
  value === undefined ||
  value === "" ||
  (Array.isArray(value) && value.length === 0) ||
  (typeof value === "object" && !Array.isArray(value) && Object.keys(value).length === 0);

const inr = (n) => `₹${Number(n).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

const primitiveText = (value) => {
  if (typeof value === "string" && ISO_DATE.test(value)) return formatDateTime(value);
  if (typeof value === "string" && HEX24.test(value)) return `…${value.slice(-6)}`;
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (value !== null && typeof value === "object") return JSON.stringify(value);
  return String(value);
};

/**
 * Turns a stored value into display data: `kind` tells the renderer whether it
 * wants a chip, a monospace id, etc.; `text` is always a safe plain fallback.
 */
export const describeValue = (field, value) => {
  const leaf = leafOf(field);
  if (isEmptyValue(value)) return { kind: "empty", text: "—" };
  if (value === "[REDACTED]") return { kind: "redacted", text: "Hidden" };

  if (typeof value === "boolean") {
    const isStatus = STATUS_FIELDS.has(leaf);
    return {
      kind: "bool",
      positive: value,
      text: isStatus ? (value ? "Active" : "Inactive") : value ? "Yes" : "No",
    };
  }

  if (Array.isArray(value)) {
    return { kind: "text", text: value.map(primitiveText).join(", ") };
  }

  if (typeof value === "object") return { kind: "json", text: JSON.stringify(value) };

  const numeric = typeof value === "number" || (typeof value === "string" && NUMERIC.test(value));
  if (numeric && PERCENT_FIELD.test(leaf)) return { kind: "text", text: `${Number(value)}%` };
  if (numeric && MONEY_FIELD.test(leaf) && !NOT_MONEY_FIELD.test(leaf)) return { kind: "text", text: inr(value) };
  if (typeof value === "number") {
    return {
      kind: "text",
      text: PLAIN_NUMBER_FIELD.test(leaf)
        ? String(value)
        : value.toLocaleString("en-IN", { maximumFractionDigits: 4 }),
    };
  }

  if (ISO_DATE.test(value)) return { kind: "text", text: formatDateTime(value) };
  if (HEX24.test(value)) return { kind: "id", text: `…${value.slice(-6)}`, title: value };
  return { kind: "text", text: value };
};

const isPlainObject = (value) => value !== null && typeof value === "object" && !Array.isArray(value);

/** `{ a: { b: 1 } }` -> `{ "a.b": 1 }`, the same path style the backend uses for changes. */
export const flattenSnapshot = (snapshot, prefix = "") =>
  Object.entries(snapshot || {}).reduce((flat, [key, value]) => {
    if (!prefix && SKIP_FIELDS.has(key)) return flat;
    const path = prefix ? `${prefix}.${key}` : key;
    if (isPlainObject(value) && Object.keys(value).length) {
      Object.assign(flat, flattenSnapshot(value, path));
    } else {
      flat[path] = value;
    }
    return flat;
  }, {});

export const sameValue = (a, b) => {
  const left = isEmptyValue(a) ? null : a;
  const right = isEmptyValue(b) ? null : b;
  return JSON.stringify(left) === JSON.stringify(right);
};

export const changeSummary = (entry) => {
  const n = entry.changes?.length || 0;
  if (n) return `${n} field${n === 1 ? "" : "s"} changed`;
  if (entry.action === "created") return "Record created";
  if (entry.action === "deleted") return "Record deleted";
  if (entry.action === "baseline") return "Starting point";
  return "—";
};
